// ** React Imports
import React, { Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { Helmet, HelmetProvider } from "react-helmet-async";

// ** Intl, CASL & ThemeColors Context
import { ToastContainer } from "react-toastify";
import { io } from "socket.io-client";
import ability from "./configs/acl/ability";
import { AbilityContext } from "./utility/context/Can";
import { ThemeContext } from "./utility/context/ThemeColors";
// import { IntlProviderWrapper } from './utility/context/Internationalization';

// ** Chart.js Configuration (must be imported before any charts are used)
import "./configs/chartjs";

// ** Global unhandled promise rejection handler
// This prevents timeout errors from appearing in the console
window.addEventListener("unhandledrejection", (event) => {
  // Check if it's a timeout-related error or auth error
  const error = event.reason;
  const errorMessage = error?.message || error?.toString() || "";

  // Suppress timeout errors and auth-related errors that are already handled
  if (
    errorMessage.includes("Timeout") ||
    errorMessage.includes("401") ||
    errorMessage.includes("Unauthorized") ||
    error?.response?.status === 401
  ) {
    event.preventDefault();
    // Optionally log to a service or do nothing
    if (import.meta.env.MODE === "development") {
      console.debug("Handled promise rejection:", errorMessage);
    }
  }
});

// eslint-disable-next-line no-unused-vars
import { store } from "./redux/storeConfig/store";
// import Spinner from './@core/components/spinner/Fallback-spinner';
import Spinner from "./components/FallbackSpinner";

import "./@core/components/ripple-button";

// ** PrismJS
import "prismjs";
import "prismjs/themes/prism-tomorrow.css";
import "prismjs/components/prism-jsx.min";

// ** React Perfect Scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";

// ** React Toastify
import "@styles/react/libs/toastify/toastify.scss";

// ** Core styles
import "./@core/assets/fonts/feather/iconfont.css";
import "./@core/scss/core.scss";
import "./assets/scss/style.scss";

// ** Service Worker
import * as serviceWorker from "./serviceWorker";
import SocketContext from "./utility/context/SocketContext";

// ** Lazy load app
const LazyApp = lazy(() => import("./App.jsx"));

const socket = io(`${import.meta.env.VITE_SERVER_URL}`, {
  // withCredentials: true, // if your backend requires credentials
});

// socket.on("connect", () => {
// 	console.log("Socket connected");
// });

// socket.on("disconnect", () => {
// 	console.log("Socket disconnected");
// });

const root = createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <SocketContext.Provider value={socket}>
      <Suspense fallback={<Spinner />}>
        {/* <PersistGate loading={null} persistor={persistor}> */}
        <HelmetProvider>
          <AbilityContext.Provider value={ability}>
            <ThemeContext>
              <Helmet>
                <title>Canteen Hub</title>
              </Helmet>
              <LazyApp />
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
              />
            </ThemeContext>
          </AbilityContext.Provider>
        </HelmetProvider>
        {/* </PersistGate> */}
      </Suspense>
    </SocketContext.Provider>
  </Provider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
